import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import Status from "../enums/Status";
import IMacroWayFeedback from "../models/macrowayFeedback/IMacroWayFeedback";
import { Alert, Button, ButtonGroup, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import BackButton from "../components/backButton/BackButton";
import validate, { DefaultValidation, IFeedbackValidation } from "./Validation";
import macroWayFeedbackService from "../services/macrowayFeedbackService";
import { FeedbackForm } from "./FeedbackForm";
import { useNavigate } from "react-router-dom";
import IDepartment from "../models/macrowayFeedback/IDepartment";
import { NIL } from "uuid";
import IDropzoneItem from "../models/dropzone/IDropzoneItem";
import fileService from "../services/fileService";

const NewFeedback: IMacroWayFeedback = ({
    departmentId: NIL,
    description: "",
    attachments: []
});

export const MacroWayFeedback = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [departments, setDepartments] = useState<IDepartment[]>([]);
    const [dropzoneFiles, setDropzoneFiles] = useState<IDropzoneItem[]>([]);
    const [feedback, setFeedback] = useState<IMacroWayFeedback>(NewFeedback);
    const [validation, setValidation] = useState<IFeedbackValidation>(DefaultValidation);

    useEffect(() => {
        getTeams(setDepartments, setStatus);
    }, []);

    const handleInputChange = (key: keyof IMacroWayFeedback, value: string) => {
        setFeedback({ ...feedback!, [key]: value });
    }

    const handleSubmit = async () => {
        setStatus(Status.NEUTRAL);
        if (!validate(feedback, setValidation)) {
            return;
        }

        setStatus(Status.SAVING);

        try {
            const attachments = await fileService.convertFilesToAttachments(dropzoneFiles.map(f => f.file));
            await macroWayFeedbackService.submitFeedback({ ...feedback, attachments });

            setStatus(Status.SAVE_SUCCESS);
            setTimeout(() => navigate("/"));
        }
        catch {
            setStatus(Status.SAVE_ERROR);
        }
    };

    const isReadOnly = [Status.SAVING, Status.SAVE_SUCCESS].includes(status);
    
    return (
        <Container className="py-3">
            <Row className="justify-content-center">
                <Col>
                    <Card className="shadow">
                        <Card.Body>
                            <Card.Title className="d-flex align-items-center mb-3">
                                <BackButton path="/" />
                                <span>{t("macroway-feedback.title")}</span>
                            </Card.Title>
                            <Card.Text as="form" method="POST" onSubmit={() => {}}>
                                
                                {
                                    status === Status.LOADING &&
                                    <div className="text-center">
                                        <Spinner />
                                    </div>
                                }

                                {
                                    status === Status.LOAD_ERROR &&
                                    <Alert variant="danger">
                                        {t("general.error")}
                                    </Alert>
                                }

                                {
                                    status !== Status.LOADING && status !== Status.LOAD_ERROR &&
                                    <FeedbackForm
                                        feedback={feedback!}
                                        departments={departments}
                                        isReadOnly={isReadOnly}
                                        onInputChange={handleInputChange}
                                        validation={validation!}
                                        dropzoneFiles={dropzoneFiles}
                                        setDropzoneFiles={setDropzoneFiles}
                                        />
                                }
                                
                                <Row>
                                    <Col>
                                        {
                                            (
                                                    status === Status.SAVE_SUCCESS &&
                                                    <Alert variant="success">
                                                        {t("macroway-feedback.success")}
                                                    </Alert>
                                            ) ||
                                            (
                                                    status === Status.SAVE_ERROR &&
                                                    <Alert variant="danger">
                                                        {t("macroway-feedback.error")}
                                                    </Alert>
                                            )
                                        }
                                    </Col>
                                </Row>
                                {
                                    ![Status.LOADING, Status.LOAD_ERROR, Status.SAVE_SUCCESS].includes(status) &&
                                    <Row>
                                        <Col className="text-center">
                                            <ButtonGroup size="lg">
                                                <Button variant="primary" type="button" className="fw-bold px-5" onClick={handleSubmit} disabled={isReadOnly}>
                                                    {t("general.submit")}
                                                </Button>
                                            </ButtonGroup>
                                        </Col>
                                    </Row>
                                }
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

async function getTeams(
    setDepartments: (departments: IDepartment[]) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const departments = await macroWayFeedbackService.getDepartments();
        setDepartments(departments);

        setStatus(Status.LOAD_SUCCESS);
    }
    catch {
        setStatus(Status.LOAD_ERROR);
    }
}